<template>
  <div>
    <div class="country">
      <select-dropdown
        ref="countrySelect"
        :value="selected.country"
        :placeholder="texts.country"
        label="CountryAlias"
        :tipsText="countryTip"
        :availableItems="searchArray.country"
        :is-load="load.country"
        required
        :nothingText="selectedError.country"
        @onSearch="onValueChange($event, 'country')"
        @selected="onSelected($event, 'country')"
        @clear="onClear('country')"
        @input="onValueChange($event, 'country')"
        @focusLost="onFocusLost('country')"
        :class="{ 'country-error': countryError || countryErrorMessage}"
      />
    </div>
    <div class="site-form_group">
      <div class="site-form_group_item">
        <select-dropdown
          ref="citySelect"
          :value="selected.city"
          :placeholder="texts.city"
          required
          label="CityAlias"
          :availableItems="searchArray.city"
          :is-load="load.city"
          :nothingText="selectedError.city"
          @onSearch="onValueChange($event, 'city')"
          @selected="onSelected($event, 'city')"
          @clear="onClear('city')"
          @focusLost="onFocusLost('city')"
        />
      </div>
      <div class="site-form_group_item postal">
        <select-postal
          ref="postCodeSelect"
          :value="selected.postal"
          :placeholder="texts.postcode"
          :availableItems="searchArray.postal"
          :is-load="load.postal"
          :nothingText="selectedError.postal"
          :tipsText="indexTip"
          :isPostal="true"
          :isPostalPostfix="isPostalPostfix"
          @input-postfix="onInputPostfix"
          @error-postfix="onErrorPostfix"
          @onSearch="onValueChange($event, 'postal')"
          @selected="onSelected($event, 'postal')"
          @clear="onClear('postal')"
          @focusLost="onFocusLost('postal')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import selectDropdown from '@/components/selectDropdown/selectDropdown.component.vue';
import selectPostal from '@/components/selectPostal/select-postal.component.vue';

export default {
  name: 'selectLocation',
  components: {
    selectDropdown,
    selectPostal,
  },
  model: {
    prop: 'location',
    event: 'change',
  },
  props: {
    countryErrorMessage: {
      type: String,
      default: '',
    },
    location: {
      type: Object,
    },
    onlyCountry: {
      type: String,
      default: '',
    },
    defaultCountry: {
      type: String,
      default: '',
    },
    isShipper: {
      type: Boolean,
      default: false,
    },
    isLocationFrom: {
      type: Boolean,
    },
  },
  data() {
    return {
      selected: {
        country: null,
        city: null,
        postal: null,
      },
      postalSelect: false,
      searchArray: {
        country: [],
        city: [],
        postal: [],
      },
      selectedError: {
        country: null,
        city: null,
        postal: null,
      },
      load: {
        country: false,
        city: false,
        postal: false,
      },
    };
  },
  computed: {
    locationSelected() {
      return (
        !!this.selected.country && !!this.selected.city && !this.countryError
      );
    },
    countryError() {
      if (this.isShipper) {
        return this.isErrorOnlyCountry || this.isErrorIsRpaAllowed;
      }
      return (
        this.isErrorOnlyCountry ||
        this.isErrorIsRpaAllowed ||
        this.isErrorOnlyToRussia
      );
    },
    isErrorIsRpaAllowed() {
      return (
        this.isShipper &&
        this.selected.country &&
        this.selected.country.IsRpaAllowed === 0
      );
    },
    isErrorOnlyCountry() {
      return (
        this.isOnlyCountry &&
        this.selected.country &&
        this.selected.country.CountryCode !== this.onlyCountry
      );
    },
    isErrorOnlyToRussia() {
      const stateGlobalFrom = this.$store.state.api.globalLocations.from;
      const stateGlobalTo = this.$store.state.api.globalLocations.to;
      if (stateGlobalFrom === null || stateGlobalTo === null) {
        return false;
      }
      const globalFrom =
        typeof stateGlobalFrom === 'string'
          ? stateGlobalFrom
          : stateGlobalFrom.CountryCode;
      const globalTo =
        typeof stateGlobalTo === 'string'
          ? stateGlobalTo
          : stateGlobalTo.CountryCode;
      return globalFrom !== 'RU' && globalTo !== 'RU';
    },
    texts() {
      return {
        country: this.$t('Country'),
        countryOnlyError: this.$t('CountryOnlyError'),
        countryNoAccess: this.$t('CountryNoAccess'),
        countryNoAccessTo: this.$t('CountryNoAccessTo'),
        city: this.$t('City'),
        postcode: this.$t('Postcode'),
        notRequired: this.$t('notRequired'),
      };
    },
    textCountryError() {
      if (this.isErrorOnlyCountry) {
        return this.texts.countryOnlyError;
      }
      if (this.isErrorOnlyToRussia && !this.isShipper) {
        return this.texts.countryNoAccessTo;
      }
      return this.texts.countryNoAccess;
    },
    isOnlyCountry() {
      return !!this.onlyCountry;
    },
    isDefaultCountry() {
      return !!this.defaultCountry;
    },
    countryTip() {
      let result;
      if (this.isLocationFrom) {
        result = this.$t('CountriesFrom');
      } else result = this.$t('CountryTo');
      if (this.isOnlyCountry) {
        result = this.$t('CountryFrom');
      }
      return result;
    },
    indexTip() {
      let result;
      if (this.isLocationFrom) {
        result = this.$t('PostalCodeFrom');
      } else result = this.$t('PostalCodeTo');
      if (this.isOnlyCountry) {
        result = this.$t('PostalCodeFrom');
      }
      return result;
    },
    isPostalPostfix() {
      const { PostfixNeeded: PostfixNeededCountry } =
        this.selected.country || {};
      const { PostfixNeeded: PostfixNeededCity } = this.selected.city || {};
      const { PostfixNeeded: PostfixNeededPostal } = this.selected.postal || {};
      return (
        PostfixNeededCountry === 1 ||
        PostfixNeededCity === 1 ||
        PostfixNeededPostal === 1
      );
    },
  },
  watch: {
    location: {
      handler: 'watchLocationHandler',
      immediate: true,
    },
  },
  mounted() {
    if (this.location === null && (this.onlyCountry || this.defaultCountry)) {
      const startCountry = this.onlyCountry || this.defaultCountry;

      const initCountryObject = {
        CountryCode: 'RU',
        DctCityName: null,
        IataCode: null,
        CommonAlias: 'Россия',
        CountryAlias: 'Россия',
        CityAlias: null,
        PostalCode: null,
        FacilityCode: null,
        NonDhlCode: null,
        WindowsTimeZoneId: null,
        PostfixNeeded: 0,
        IsRpaAllowed: 1,
      };

      setTimeout(() => {
        this.onSelected(initCountryObject, 'country', true);
        this.setGlobalCountry(startCountry);
      }, 500);
    } else {
      this.setLocation(this.location);
      this.setGlobalCountry(this.location);
    }
  },
  methods: {
    ...mapActions('preparation', ['resetCalculation']),
    setGlobalCountry(location) {
      if (this.isShipper) {
        this.$store.dispatch('api/setLocationFrom', location);
      } else {
        this.$store.dispatch('api/setLocationTo', location);
      }
    },
    watchLocationHandler(val) {
      if (typeof val === 'object' && val !== null) {
        this.selected.country = val;
        this.selected.city = val;
      }
    },
    onClear(entity) {
      this.selected[entity] = null;
      this.selectedError[entity] = null;
      switch (entity) {
        case 'country':
          this.$refs.citySelect.onClearClick();
          this.setGlobalCountry(null);
          break;
        case 'city':
          this.$refs.postCodeSelect.onClearClick();
          break;
        case 'postal':
          this.postalSelect = false;
          break;
        default:
      }

      this.onChangeLocation();
    },
    onFocusLost(entity, resetDropdown) {
      this.load[entity] = false;
      if (resetDropdown) {
        this.searchArray[entity] = [];
      }
      this.selectedError[entity] = null;
    },
    onSelected(value, entity, isOnlyCountry, isChangePostalBody = true) {
      this.setGlobalCountry(value.CountryCode);
      if (
        isOnlyCountry === true &&
        (this.selected.country || this.selected.city)
      ) {
        return;
      }

      const isPostfix = value.PostfixNeeded === 1;
      if (isPostfix && entity !== 'country') {
        const { PostalCode, Postfix } = value;
        let trimPostalCode = PostalCode;
        if (typeof trimPostalCode === 'string') {
          trimPostalCode = PostalCode.trim();
        }
        const postalBody =
          isChangePostalBody === true
            ? trimPostalCode.slice(0, -2)
            : trimPostalCode;
        let postalPostfix = trimPostalCode.slice(-2);
        postalPostfix = postalPostfix.toUpperCase();
        if (
          typeof Postfix === 'string' &&
          !(Postfix === 'XX' || Postfix === '')
        ) {
          postalPostfix = Postfix;
        }

        this.selected[entity] = {
          ...value,
          PostalCode: postalBody,
          Postfix: postalPostfix,
        };
      } else {
        this.selected[entity] = value;
      }

      switch (entity) {
        case 'country':
          this.selected.postal = null;
          this.postalSelect = false;
          this.selected.city = null;
          break;
        case 'city':
          this.selected.postal = null;
          this.postalSelect = false;
          if (!this.selected.country) {
            this.selected.country = value;
          }
          break;
        case 'postal':
          if (!this.selected.country) {
            this.selected.country = value;
          }
          if (!this.selected.city) {
            this.selected.city = value;
          }
          this.postalSelect = true;
          break;
        default:
      }

      this.onChangeLocation();
    },
    async onValueChange(value, entity) {
      this.searchArray[entity] = [];
      this.selectedError[entity] = null;

      if (value) {

        if (typeof value === 'string' && value.trim().length < 3) {
          return;
        }
        this.load[entity] = true;

        const autoCompleteRequest = {
          searchString: value,
          requestedEntity: entity,
        };

        switch (entity) {
          case 'country':
            break;
          case 'city':
            autoCompleteRequest.countryCode = this.selected.country
              ? this.selected.country.CountryCode
              : null;
            break;
          case 'postal':
            this.selectedError.country = null;
            autoCompleteRequest.city = this.selected.city
              ? this.selected.city.DctCityName
              : null;
            autoCompleteRequest.countryCode = this.selected.country
              ? this.selected.country.CountryCode
              : null;
            autoCompleteRequest.requestedEntity = 'zip';
            break;
          default:
        }

        this.searchArray[entity] = await this.$api.autocomplete(
          autoCompleteRequest,
        );

        if (this.searchArray[entity].length === 0) {
          this.selectedError[entity] = this.$t('nothingText');
          setTimeout(() => {
            this.selectedError[entity] = null;
          }, 2000);
        }
        this.load[entity] = false;
      } else {
        this.onClear(entity);
      }
    },
    onChangeLocation() {
      this.resetCalculation();
      let newLocation = null;

      if (this.locationSelected) {
        const cityPostalCode = this.selected.city.PostalCode;

        if (!this.postalSelect && !this.selected.postal) {
          this.selected.city.PostalCode = null;
        }

        newLocation = {
          ...this.selected.country,
          ...this.selected.city,
          ...this.selected.postal,
        };

        const autoPostal = this.location ? this.location.autoPostal : null;

        newLocation.autoPostal = cityPostalCode || autoPostal;
      }
      this.$emit('change', newLocation);
    },
    setLocation(newLocation) {
      if (newLocation) {
        if (newLocation.CountryAlias) {
          this.onSelected({ ...newLocation }, 'country');
        }
        if (newLocation.CityAlias) {
          this.onSelected({ ...newLocation }, 'city');
        }
        if (newLocation.PostalCode) {
          this.onSelected({ ...newLocation }, 'postal', false, false);
        }
      }
    },
    onInputPostfix(value) {
      this.selected.postal = value;
      this.onChangeLocation();
    },
    onErrorPostfix() {},
  },
};
</script>

<style scoped lang="scss">
.country {
  position: relative;
}

.error-country {
  position: absolute;
  top: 96%;
  width: 100%;
  padding: 0 8px;
  font-size: 11px;
  color: #f00;
  background: white;
  border-radius: 0 0 8px 8px;
  z-index: 10;
}

.country-error {
  background: rgba(255, 0, 0, 0.7);
}

.postal {
  position: relative;

  &-label {
    position: absolute;
    top: 80%;
    font-size: 11px;
    color: #999;
    margin-left: 1px;
  }
}
</style>
